import React, { useCallback, useState } from 'react';
import { type Accept, type FileRejection, type FileWithPath, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  Box,
  Button,
  ButtonBase,
  dialogContentClasses,
  LinearProgress,
  linearProgressClasses,
  paperClasses,
  Stack,
  type SxProps,
  Typography,
  useTheme
} from '@mui/material';
import { type Theme } from '@mui/material/styles';
import { omit, uniqueId } from 'lodash';
import { mergeSx } from 'merge-sx';
import { useToggle } from 'usehooks-ts';

import {
  FileMetadataDTO,
  getGetTalentByIdQueryKey,
  getListProcessTalentFilesQueryKey,
  useDeleteFile,
  useGetPresignedDownloadUrlHook,
  useListProcessTalentFiles,
  useUploadFileHook
} from '@/api/generated';
import DocumentIcon from '@/assets/icons/document.svg?react';
import DocumentDownloadIcon from '@/assets/icons/document-download.svg?react';
import DocumentUploadIcon from '@/assets/icons/document-upload.svg?react';
import PaperClip from '@/assets/icons/paper-clip.svg?react';
import TrashBinIcon from '@/assets/icons/trash-bin.svg?react';
import XSmallIcon from '@/assets/icons/x-small.svg?react';
import Badge from '@/components/Badge';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import Loader from '@/components/Loader/Loader';
import { useSnackbar } from '@/hooks/useSnackbar';
import SettingsModalForm from '@/views/SettingsModalForm/SettingsModalForm';

import { useTalentProcessContext } from './TalentProcessContext';

const MAX_FILE_LIMIT = 7;

type DroppedItem = { file: FileWithPath; id: string; errors?: FileRejection['errors']; status?: DroppedFileStatus };

const FilePicker = ({ sx }: { sx?: SxProps<Theme> }) => {
  const { palette } = useTheme();
  const { process, talent } = useTalentProcessContext();
  const [isOpen, toggleOpen] = useToggle(false);

  return (
    <>
      <Button
        variant="text-icon"
        onClick={toggleOpen}
        disableRipple={true}
        startIcon={
          <Badge color="success" variant="dot" invisible={!talent.supportedFiles?.[process.id!]?.length}>
            <PaperClip />
          </Badge>
        }
        sx={mergeSx({ color: palette.primary.main }, sx)}
      />
      {isOpen && <FileUploadModal onClose={toggleOpen} />}
    </>
  );
};

const FileUploadModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation(['processes', 'common']);
  const { palette, typography, spacing } = useTheme();
  const { process, talent } = useTalentProcessContext();
  const queryClient = useQueryClient();
  const [droppedFiles, setDroppedFiles] = useState<DroppedItem[]>([]);
  const [successFiles, setSuccessFiles] = useState<DroppedItem[]>([]);
  const uploadFile = useUploadFileHook();
  const { showMessage } = useSnackbar();
  const {
    data: { files: uploadedFiles = [] } = {},
    isLoading: isLoadingFiles,
    isRefetching
  } = useListProcessTalentFiles(process.id!, talent.id!, {
    query: { suspense: false, staleTime: Infinity }
  });
  const uploadedCount = uploadedFiles.length + successFiles.length;
  const droppedValidFilesCount = droppedFiles.filter(item => !item.errors).length;

  const handleFilesUpload = useCallback(
    (droppedItems: DroppedItem[]) => {
      const draftValidItems = droppedItems.filter(
        ({ status, errors }) => status === DroppedFileStatus.DRAFT && !errors
      );

      setDroppedFiles(df =>
        df.map(item =>
          item.status === DroppedFileStatus.DRAFT && !item.errors
            ? { ...item, status: DroppedFileStatus.PENDING }
            : item
        )
      );

      Promise.all(
        draftValidItems.map(async fileItem => {
          try {
            const { id } = await uploadFile(process.id!, talent.id!, { file: fileItem.file });
            setDroppedFiles(df => df.filter(stateFile => stateFile.id !== fileItem.id));
            setSuccessFiles(sf => [...sf, { ...fileItem, id: id!, status: DroppedFileStatus.SUCCESS }]);
            queryClient.invalidateQueries(getGetTalentByIdQueryKey(talent.id!));
          } catch (e) {
            setDroppedFiles(df =>
              df.map(file =>
                file.id === fileItem.id
                  ? {
                      ...omit(file, `status`),
                      errors: [{ code: 'upload-error', message: '' }]
                    }
                  : file
              )
            );
          }
        })
      );
    },
    [process.id, queryClient, talent.id, uploadFile]
  );

  const handleDrop = useCallback(
    async (validFiles: FileWithPath[], fileRejections: FileRejection[]) => {
      const invalidItems: DroppedItem[] = fileRejections.map(item => ({
        file: item.file,
        errors: item.errors,
        id: uniqueId()
      }));
      const validItems: DroppedItem[] = validFiles.map(item => ({
        file: item,
        id: uniqueId(),
        status: DroppedFileStatus.DRAFT
      }));

      const droppedItems = [...droppedFiles, ...invalidItems, ...validItems];

      if (droppedItems.length + uploadedCount > MAX_FILE_LIMIT) {
        showMessage(
          t('layouts.process.uploadTalentFiles.dropzone.maxLimitExceed', { maxAllowed: MAX_FILE_LIMIT - uploadedCount })
        );

        return;
      }

      setDroppedFiles(droppedItems);
      handleFilesUpload(droppedItems);
    },
    [droppedFiles, handleFilesUpload, showMessage, t, uploadedCount]
  );
  const {
    getRootProps,
    getInputProps,
    open: openFileExplorer
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: 2 * 1024 * 1024, // 2mb
    accept: acceptMimeTypes,
    onDrop: handleDrop
  });

  const handleRemoveFile = useCallback(
    (fileId: string) => {
      const items = droppedFiles.filter(stateFile => stateFile.id !== fileId);

      setDroppedFiles(items);
    },
    [droppedFiles]
  );

  const handleOnDeleteSuccess = useCallback(
    (fileId: string) => {
      const isSuccessFile = successFiles.some(({ id }) => id === fileId);

      queryClient.invalidateQueries(getGetTalentByIdQueryKey(talent.id!));

      if (isSuccessFile) {
        setSuccessFiles(successFiles.filter(({ id }) => id !== fileId));

        return;
      }

      queryClient.invalidateQueries(getListProcessTalentFilesQueryKey(process.id!, talent.id!));
    },
    [process.id, queryClient, successFiles, talent.id]
  );

  const handleCloseModal = useCallback(() => {
    onClose();
    queryClient.invalidateQueries(getListProcessTalentFilesQueryKey(process.id!, talent.id!));
  }, [onClose, process.id, queryClient, talent.id]);

  return (
    <SettingsModalForm
      isOpen={true}
      onClose={handleCloseModal}
      locationsMatch={() => false}
      head={
        <Typography component="h2" variant="h3">
          {t('layouts.process.uploadTalentFiles.title')}
        </Typography>
      }
      sx={{
        [`& .${paperClasses.root}`]: {
          margin: 0,
          width: spacing(60),
          height: 'unset'
        },

        [`& .${dialogContentClasses.root}`]: {
          paddingBottom: spacing(3)
        }
      }}
    >
      <>
        {isLoadingFiles && !isRefetching ? (
          <Loader sx={{ mx: 'auto' }} />
        ) : (
          <>
            <Box
              {...getRootProps()}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                marginX: spacing(2),
                paddingY: spacing(3),
                borderRadius: spacing(0.5),
                backgroundColor: palette.primary[50],

                '& .dashed-border': {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  zIndex: 0
                }
              }}
            >
              <input {...getInputProps()} />
              <svg className="dashed-border" pointerEvents="none" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect
                  x="1px"
                  y="1px"
                  width="calc(100% - 2px)"
                  height="calc(100% - 2px)"
                  rx="3.5"
                  stroke={palette.primary[500]}
                  strokeDasharray="8 8"
                  strokeDashoffset="1"
                />
              </svg>
              <Box
                component={DocumentUploadIcon}
                sx={{
                  display: 'block',
                  width: spacing(7),
                  height: spacing(7),
                  color: palette.primary.main
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: spacing(2),
                  fontWeight: typography.fontWeightMedium
                }}
              >
                {t('layouts.process.uploadTalentFiles.dropzone.title')}
                &nbsp;
                <ButtonBase
                  disableRipple={true}
                  sx={{
                    textDecoration: 'underline',
                    color: palette.primary.main,
                    ...typography.body2,
                    fontWeight: typography.fontWeightMedium
                  }}
                  onClick={openFileExplorer}
                >
                  {t('common:files.browse')}
                </ButtonBase>
              </Typography>
              <Typography variant="body3" sx={{ color: 'grey.800', textAlign: 'center', marginTop: spacing(1) }}>
                {t('layouts.process.uploadTalentFiles.dropzone.supportedFiles')}
              </Typography>
            </Box>
            {droppedFiles.length > 0 && (
              <>
                <Typography
                  component="p"
                  variant="body3"
                  color="grey.800"
                  sx={{ marginX: spacing(2), marginTop: spacing(2) }}
                >
                  {t('layouts.process.uploadTalentFiles.uploading', {
                    inProgress: droppedValidFilesCount,
                    total: MAX_FILE_LIMIT - uploadedCount
                  })}
                </Typography>
                {droppedFiles.map(droppedFile => (
                  <FileItem
                    key={droppedFile.id}
                    fileItem={droppedFile}
                    onRemove={handleRemoveFile}
                    processId={process.id!}
                    talentId={talent.id!}
                    sx={{
                      marginX: spacing(2)
                    }}
                  />
                ))}
              </>
            )}
            {uploadedCount > 0 && (
              <>
                <Typography
                  component="p"
                  variant="body3"
                  color="grey.800"
                  sx={{ marginX: spacing(2), marginTop: spacing(2) }}
                >
                  {t('layouts.process.uploadTalentFiles.uploaded', {
                    uploaded: uploadedCount,
                    maxAllowed: MAX_FILE_LIMIT
                  })}
                </Typography>
                {uploadedFiles.map(file => (
                  <FileItem
                    key={file.id}
                    fileItem={file}
                    onDeleteSuccess={handleOnDeleteSuccess}
                    processId={process.id!}
                    talentId={talent.id!}
                    sx={{
                      marginX: spacing(2)
                    }}
                  />
                ))}
                {successFiles.map(file => (
                  <FileItem
                    key={file.id}
                    fileItem={file}
                    onDeleteSuccess={handleOnDeleteSuccess}
                    processId={process.id!}
                    talentId={talent.id!}
                    sx={{
                      marginX: spacing(2)
                    }}
                  />
                ))}
              </>
            )}
          </>
        )}
      </>
    </SettingsModalForm>
  );
};

enum DroppedFileStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  DRAFT = 'DRAFT'
}

const acceptMimeTypes = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/pdf': ['.pdf'],
  'text/plain': ['.txt'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
} as const satisfies Accept;

interface FileItemProps {
  fileItem: FileMetadataDTO | DroppedItem;
  onRemove?: (fileId: string) => void;
  onDeleteSuccess?: (fileId: string) => void;
  processId?: string;
  talentId?: string;
  sx?: SxProps<Theme>;
}

const FileItem = ({ fileItem, onRemove, onDeleteSuccess, processId, talentId, sx }: FileItemProps) => {
  const { t } = useTranslation('processes');
  const { spacing, palette } = useTheme();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();
  const isDroppedFileItem = 'file' in fileItem;
  const fileName = isDroppedFileItem ? fileItem.file.name : fileItem.fileName;
  const error =
    'errors' in fileItem &&
    fileItem.errors &&
    t(`layouts.process.uploadTalentFiles.dropzone.${fileItem.errors[0].code}`);
  const status = 'status' in fileItem && fileItem.status;
  const isReadOnly = 'isReadOnly' in fileItem && fileItem.isReadOnly;
  const getPresignedDownloadLink = useGetPresignedDownloadUrlHook();
  const [isConfirmDeleteOpen, toggleConfirmDelete] = useToggle();
  const handleFileDownload = useCallback(async () => {
    if (!processId || !talentId) return;

    const { presignedUrl } = await getPresignedDownloadLink(processId, talentId, fileItem.id!);

    if (!presignedUrl) {
      showMessage(t('common:error.somethingWentWrong'));

      return;
    }

    window.open(presignedUrl);
  }, [fileItem.id, getPresignedDownloadLink, processId, showMessage, t, talentId]);

  const { mutateAsync: deleteFile, isLoading: isDeleting } = useDeleteFile();
  const handleFileDelete = useCallback(async () => {
    if (!processId || !talentId) return;

    try {
      await deleteFile({ processId: processId, talentId: talentId, fileId: fileItem.id! });
      onDeleteSuccess?.(fileItem.id!);
    } catch {
      showMessage(t('common:error.somethingWentWrong'));

      return;
    }
    queryClient.invalidateQueries([
      getListProcessTalentFilesQueryKey(processId, talentId),
      getGetTalentByIdQueryKey(talentId)
    ]);
  }, [deleteFile, fileItem.id, onDeleteSuccess, processId, queryClient, showMessage, t, talentId]);

  return (
    <Box sx={sx}>
      <Stack
        direction="row"
        sx={mergeSx(
          {
            position: 'relative',
            alignItems: 'center',
            marginTop: spacing(1),
            padding: spacing(0.75, 1),
            borderRadius: spacing(0.5),
            backgroundColor: palette.grey[100]
          },
          !!error && {
            border: `1px solid ${palette.error.main}`,
            backgroundColor: palette.background.default
          },
          !isDroppedFileItem && {
            backgroundColor: palette.primary[50]
          },
          status === DroppedFileStatus.SUCCESS && {
            border: `1px solid ${palette.green.main}`,
            backgroundColor: palette.green[50]
          }
        )}
      >
        <Box
          component={DocumentIcon}
          sx={{
            display: 'block',
            width: spacing(2.5),
            height: spacing(2.5)
          }}
        />
        <Typography variant="body3" noWrap marginInlineStart={spacing(0.5)}>
          {fileName}
        </Typography>
        {status === DroppedFileStatus.PENDING && <FileItemLoadingStatus />}
        {isDroppedFileItem && onRemove && (
          <Button
            variant="text-icon"
            size="small"
            disableRipple={true}
            startIcon={<XSmallIcon />}
            onClick={() => onRemove(fileItem.id)}
            sx={{ marginInlineStart: 'auto', color: 'grey.600' }}
          />
        )}
        {(!isDroppedFileItem || status === DroppedFileStatus.SUCCESS) && (
          <Box sx={{ marginInlineStart: 'auto' }}>
            {processId && talentId && (
              <DropdownMenu
                buttonProps={{ sx: { color: 'grey.600', width: spacing(2), height: spacing(2), padding: 0 } }}
              >
                <DropdownMenuItem
                  text={t('layouts.process.uploadTalentFiles.download')}
                  icon={<DocumentDownloadIcon width={20} height={20} />}
                  onClick={handleFileDownload}
                />
                <DropdownMenuItem
                  disabled={isDeleting || isReadOnly}
                  text={t('layouts.process.uploadTalentFiles.delete')}
                  icon={<TrashBinIcon width={20} height={20} />}
                  onClick={toggleConfirmDelete}
                  sx={{ color: palette.red.main }}
                />
                <ConfirmationModal
                  open={isConfirmDeleteOpen}
                  isLoading={isDeleting}
                  onConfirm={handleFileDelete}
                  onDiscard={toggleConfirmDelete}
                  title={t('layouts.process.uploadTalentFiles.deleteConfirmation', {
                    fileName
                  })}
                  confirmButtonLabel={t('layouts.process.uploadTalentFiles.delete')}
                />
              </DropdownMenu>
            )}
          </Box>
        )}
      </Stack>
      <Typography variant="body3" color="error">
        {error}
      </Typography>
    </Box>
  );
};

export default FilePicker;

const FileItemLoadingStatus = () => (
  <LinearProgress
    sx={({ spacing }) => ({
      position: 'absolute',
      insetInlineStart: 0,
      bottom: 0,
      width: '100%',
      backgroundColor: 'transparent',
      borderBottomRightRadius: spacing(0.5),
      borderBottomLeftRadiusRadius: spacing(0.5),

      [`& .${linearProgressClasses.bar}`]: {
        borderRadius: spacing(0.5)
      }
    })}
  />
);
